import React, { useEffect, useState, useContext } from "react";
import { Table, Spinner, Alert, Container, Card, Button, Badge, Row, Col, Collapse } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "../components/HusariaHeader";

const SofiaStatus = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const [gateways, setGateways] = useState([]);
    const [sofiaProfiles, setSofiaProfiles] = useState([]);
    const [inactiveItems, setInactiveItems] = useState([]);
    const navigate = useNavigate();
    const [expandedProfile, setExpandedProfile] = useState(null);
    const [statusText, setStatusText] = useState("System operational");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    const fetchSofiaStatus = async () => {
        setLoading(true);
        setError(null);
        try {
            const sofia_resp = await fetch(`${serverHost}/sofia_status`);
            if (!sofia_resp.ok) throw new Error("Failed to fetch Sofia status");
            const sofia_profiles = await sofia_resp.json();
            setGateways(sofia_profiles.gateways || []);
            setSofiaProfiles(sofia_profiles.profiles || [])

            const status_resp = await fetch(`${serverHost}/fs_status`);
            if (!status_resp.ok) throw new Error("Failed to fetch Freeswitch status");
            const fs_status = await status_resp.json();
            setStatusText(fs_status.result || "System operational");
        
            console.log("Refreshing Sofia status...");
            setLastRefresh(new Date());

        } catch (err) {
            setError(err.message);
            setStatusText("Error fetching status");
        } finally {
            setLoading(false)
        }
    };

    const handleFlushCache = async () => {
        try {
            // const response = await fetch(`${serverHost}/flush_cache`, { method: "POST" });
            // if (!response.ok) throw new Error("Failed to flush cache");
            setStatusText("Cache flushed successfully");
            console.log("Flush cache triggered");
        } catch (err) {
            setError(err.message);
            setStatusText("Failed to flush cache");
        }
    };

    const handleReloadACL = async () => {
        try {
        // const response = await fetch(`${serverHost}/reload_acl`, { method: "POST" });
        // if (!response.ok) throw new Error("Failed to reload ACL");
        setStatusText("ACL reloaded successfully");
        console.log("Reload ACL triggered");
        } catch (err) {
        setError(err.message);
        setStatusText("Failed to reload ACL");
        }
    };

    const handleReloadXML = async () => {
        try {
        // const response = await fetch(`${serverHost}/reload_xml`, { method: "POST" });
        // if (!response.ok) throw new Error("Failed to reload XML");
        setStatusText("XML configuration reloaded successfully");
        console.log("Reload XML triggered");
        } catch (err) {
        setError(err.message);
        setStatusText("Failed to reload XML");
        }
    };

    const handleProfileAction = async (profileName, action) => {
        try {
        const response = await fetch(`${serverHost}/sofia_action/${profileName}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        });
        if (!response.ok) throw new Error(`Failed to ${action} profile`);
        
            if (action === "stop") {
                // Find the profile and add to inactive list
                const profile = sofiaProfiles.find(p => p.name === profileName);
                if (profile) {
                    setInactiveItems(prev => [...prev, { ...profile, itemType: "profile" }]);
                }
            }
            
            setStatusText(`Profile ${profileName} ${action} initiated`);
            console.log(`${action} profile ${profileName}`);
        } catch (err) {
            setError(err.message);
            setStatusText(`Failed to ${action} profile ${profileName}`);
        }
    };

    const handleStopGateway = async (gateway_uuid) => {
        try {
            const resp = await fetch(`${serverHost}/stop_gw/${gateway_uuid}`, {
                method: "DELETE"
            })
            if (!resp.ok) throw new Error(`Failed to kill gateway`);
            
            // Find the gateway and add to inactive list
            const gateway = gateways.find(g => g.uuid === gateway_uuid);
            if (gateway) {
                setInactiveItems(prev => [...prev, { ...gateway, itemType: "gateway" }]);
            }
            
            setStatusText(`Gateway ${gateway_uuid} kill initiated`);
            console.log(`Killed gateway ${gateway_uuid}`);
        } catch (err) {
            setError(err.message);
            setStatusText(`Failed to kill gateway ${gateway_uuid}`);
        }
    };

    const handleStartItem = async (item) => {
        try {
            if (item.itemType === "profile") {
                // Start profile
                // const response = await fetch(`${serverHost}/sofia_profile_action`, {
                //   method: "POST",
                //   headers: { "Content-Type": "application/json" },
                //   body: JSON.stringify({ profile: item.name, action: "start" }),
                // });
                // if (!response.ok) throw new Error(`Failed to start profile`);
                
                setStatusText(`Profile ${item.name} started`);
                console.log(`Started profile ${item.name}`);
            } else if (item.itemType === "gateway") {
                // Start gateway
                // const response = await fetch(`${serverHost}/start_gw/${item.uuid}`, {
                //   method: "POST"
                // });
                // if (!response.ok) throw new Error(`Failed to start gateway`);
                
                setStatusText(`Gateway ${item.name} started`);
                console.log(`Started gateway ${item.name}`);
            }
            
            // Remove from inactive list
            setInactiveItems(prev => prev.filter(i => 
                item.itemType === "profile" ? i.name !== item.name : i.uuid !== item.uuid
            ));
        } catch (err) {
            setError(err.message);
            setStatusText(`Failed to start ${item.itemType} ${item.name}`);
        }
    };

    const toggleProfileDetails = (profileName) => {
        setExpandedProfile(expandedProfile === profileName ? null : profileName);
    };

    useEffect(() => {
        fetchSofiaStatus();
    }, []);

    const getStateBadge = (state) => {
        const stateLower = state?.toLowerCase();
        if (stateLower === "running") return <Badge bg="success">RUNNING</Badge>;
        if (stateLower === "stopped") return <Badge bg="danger">STOPPED</Badge>;
        if (stateLower === "paused") return <Badge bg="warning" text="dark">PAUSED</Badge>;
        return <Badge bg="success" >{state || "UNKNOWN"}</Badge>;
    };

    const runningCount = sofiaProfiles.filter(p => p.state?.toLowerCase() === "running").length;
    const stoppedCount = sofiaProfiles.filter(p => p.state?.toLowerCase() === "stopped").length;

    return (
        <>
        <HusariaHeader name="Sofia Status" parentName="Dealer" padding="9rem"/>

        <Container fluid className="mt--8">
            {/* Status Text Display */}
            <div style={{marginBottom: "8px"}}>
            <span className="return-dealer-menu" onClick={() => navigate('/admin/dealer-menu')}>
                <i class="fas fa-caret-left"></i> Return to Dealer Menu
            </span>
            </div>
            <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light border-dark" : ""}`} 
                style={{
                    borderRadius: "1rem",
                    overflow: "hidden", 
                }}
            >
            <Card.Body style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                <div className="d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="mb-1" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                    System Status
                    </h5>
                    <p className="mb-0" style={{ color: isDark ? "#9ca3af" : "#6c757d", fontSize: "0.9rem", whiteSpace: "pre-line", }}>
                    {statusText}
                    </p>
                    <small className="text-muted">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                    </small>
                </div>
                {loading && <Spinner animation="border" size="sm" variant={isDark ? "light" : "primary"} />}
                </div>
            </Card.Body>
            </Card>

             {/* Sofia Profiles Table */}
            <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light border-dark" : ""}`}>
            <Card.Header
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                color: isDark ? "#e2e8f0" : "#111",
                }}
            >
                <h4 className="mb-2 mb-md-0">SOFIA PROFILES</h4>
                <div className="d-flex gap-2 flex-wrap">
                <Button
                    variant="danger"
                    size="sm"
                    onClick={handleFlushCache}
                    disabled={loading}
                >
                    Flush Cache
                </Button>
                <Button
                    variant="warning"
                    size="sm"
                    onClick={handleReloadACL}
                    disabled={loading}
                >
                    Reload ACL
                </Button>
                <Button
                    variant="light"
                    size="sm"
                    onClick={handleReloadXML}
                    disabled={loading}
                >
                    Reload XML
                </Button>
                <Button
                    variant="info"
                    size="sm"
                    onClick={fetchSofiaStatus}
                    disabled={loading}
                >
                    {loading ? "Refreshing..." : "Refresh"}
                </Button>
                </div>
            </Card.Header>

            <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                {error ? (
                <Alert variant="danger" className="m-3">
                    {error}
                </Alert>
                ) : sofiaProfiles.length === 0 ? (
                <div
                    className="text-center py-5"
                    style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                >
                    <h5>No Sofia profiles found</h5>
                    <p className="mb-0">There are currently no Sofia profiles configured.</p>
                </div>
                ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table
                    striped
                    hover
                    responsive
                    bordered
                    className="mb-0"
                    style={{
                        minWidth: "1000px",
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                    >
                    <thead
                        style={{
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        fontWeight: "bold",
                        color: isDark ? "#e2e8f0" : "#212529",
                        }}
                    >
                        <tr>
                        <th className="text-center">Name</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Data</th>
                        <th className="text-center">State</th>
                        <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sofiaProfiles.map((profile, index) => (
                        <React.Fragment key={index}>
                            {/* Main Profile Row */}
                            <tr role="button" onClick={() => toggleProfileDetails(profile.name)} >
                            <td 
                                className="text-center" 
                                style={{ 
                                fontWeight: 600, 
                                cursor: "pointer",
                                userSelect: "none"
                                }}
                            >
                                <span style={{ marginRight: "8px", fontSize: "0.9rem" }}>
                                {expandedProfile === profile.name ? "▼" : "▶"}
                                </span>
                                {profile.name || "-"}
                            </td>
                            <td className="text-center">
                                <Badge bg="info">{profile.type || "profile"}</Badge>
                            </td>
                            <td className="text-center" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                                {profile.data || "-"}
                            </td>
                            <td className="text-center">{getStateBadge(profile.state)}</td>
                            <td className="text-center">
                                <div className="d-flex gap-1 justify-content-center flex-wrap">
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleProfileAction(profile.name, "flush-registrations")
                                    }}
                                >
                                    Flush Reg
                                </Button>
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleProfileAction(profile.name, "registrations")
                                    }}
                                >
                                    Registrations
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleProfileAction(profile.name, "stop")
                                    }}
                                >
                                    Stop
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation() 
                                        handleProfileAction(profile.name, "restart")
                                    }}
                                >
                                    Restart
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleProfileAction(profile.name, "rescan")
                                    }}
                                >
                                    Rescan
                                </Button>
                                </div>
                            </td>
                            </tr>
                            
                            {/* Expanded Details Row - Only for profiles */}
                            {expandedProfile === profile.name && (
                            <tr className="no-hover">
                                <td 
                                colSpan="5" 
                                style={{ 
                                    padding: 0,
                                    backgroundColor: isDark ? "#111827" : "#f8f9fa"
                                }}
                                >
                                <div style={{ padding: "20px" }}>
                                    <h4 style={{ color: isDark ? "#e2e8f0" : "#212529", marginBottom: "15px" }}>
                                    Profile Details
                                    </h4>
                                    <Table
                                    bordered
                                    size="sm"
                                    style={{
                                        backgroundColor: isDark ? "#1f2937" : "#fff",
                                        color: isDark ? "#e2e8f0" : "#212529",
                                        marginBottom: 0
                                    }}
                                    >
                                    <tbody>
                                        {profile.details?.map((detail, idx) => (
                                        <tr key={idx} >
                                            <td style={{ 
                                                fontWeight: 600, 
                                                width: "30%",
                                                backgroundColor: isDark ? "#374151" : "#f8f9fa",
                                                color: isDark ? "#e2e8f0" : "#212529"
                                            }}>
                                            {detail.key}
                                            </td>
                                            <td style={{ 
                                            fontFamily: "monospace", 
                                            fontSize: "0.85rem",
                                            color: isDark ? "#d1d5db" : "#495057"
                                            }}>
                                            {detail.value}
                                            </td>
                                        </tr>
                                        ))}
                                    </tbody>
                                    </Table>
                                </div>
                                </td>
                            </tr>
                            )}
                        </React.Fragment>
                        ))}
                    </tbody>
                    </Table>
                </div>
                )}
            </Card.Body>
            </Card>

            {/* Gateways Table */}
            <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light border-dark" : ""}`}>
            <Card.Header
                style={{
                backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                color: isDark ? "#e2e8f0" : "#111",
                }}
            >
                <h4 className="mb-0">GATEWAYS</h4>
            </Card.Header>

            <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                {gateways.length === 0 ? (
                <div
                    className="text-center py-5"
                    style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                >
                    <h5>No gateways found</h5>
                    <p className="mb-0">There are currently no gateways configured.</p>
                </div>
                ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table
                    striped
                    hover
                    responsive
                    bordered
                    className="mb-0"
                    style={{
                        minWidth: "1000px",
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                    >
                    <thead
                        style={{
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        fontWeight: "bold",
                        color: isDark ? "#e2e8f0" : "#212529",
                        }}
                    >
                        <tr>
                        <th className="text-center">Name</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Data</th>
                        <th className="text-center">State</th>
                        <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {gateways.map((gateway) => (
                        <tr key={gateway.uuid} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                            <td className="text-center" style={{ fontWeight: 600 }}>
                            {gateway.name || "-"}
                            </td>
                            <td className="text-center">
                            <Badge bg="primary">{gateway.type || "gateway"}</Badge>
                            </td>
                            <td className="text-center" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                            {gateway.data || "-"}
                            </td>
                            <td className="text-center">{getStateBadge(gateway.state)}</td>
                            <td className="text-center">
                                <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleStopGateway(gateway.uuid)}
                                >
                                Stop
                                </Button>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                    </Table>
                </div>
                )}
            </Card.Body>
            </Card>

            {/* Inactive Profiles Table */}
            <Card className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}>
            <Card.Header
                style={{
                backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                color: isDark ? "#e2e8f0" : "#111",
                }}
            >
                <h4 className="mb-0">INACTIVE PROFILES</h4>
            </Card.Header>

            <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                {inactiveItems.length === 0 ? (
                <div
                    className="text-center py-5"
                    style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                >
                    <h5>No inactive items</h5>
                    <p className="mb-0">All profiles and gateways are currently active.</p>
                </div>
                ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table
                    striped
                    hover
                    responsive
                    bordered
                    className="mb-0"
                    style={{
                        minWidth: "1000px",
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                    }}
                    >
                    <thead
                        style={{
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        fontWeight: "bold",
                        color: isDark ? "#e2e8f0" : "#212529",
                        }}
                    >
                        <tr>
                        <th className="text-center">Name</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Data</th>
                        <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inactiveItems.map((item, index) => (
                        <tr key={index} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                            <td className="text-center" style={{ fontWeight: 600 }}>
                            {item.name || "-"}
                            </td>
                            <td className="text-center">
                            <Badge bg={item.itemType === "profile" ? "info" : "primary"}>
                                {item.itemType || "-"}
                            </Badge>
                            </td>
                            <td className="text-center" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                            {item.data || "-"}
                            </td>
                            <td className="text-center">
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleStartItem(item)}
                            >
                                Start
                            </Button>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                    </Table>
                </div>
                )}
            </Card.Body>
            </Card>
        </Container>
        </>
    );
};

export default SofiaStatus;